import { useState, useMemo } from 'react';
import { StatusBadge } from '@/components/base/StatCard';
import { surveys, type Survey, type SurveyQuestion, type QuestionType } from '@/mocks/surveys';
import { customers, type Customer } from '@/mocks/customers';

const questionTypeLabels: Record<QuestionType, string> = {
  rating: 'Puanlama (1-5)',
  multiple_choice: 'Çoktan Seçmeli',
  text: 'Yazı Alanı',
  yes_no: 'Evet / Hayır',
};

const questionTypeIcons: Record<QuestionType, string> = {
  rating: 'ri-star-line',
  multiple_choice: 'ri-checkbox-multiple-line',
  text: 'ri-text',
  yes_no: 'ri-toggle-line',
};

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

interface SendLog {
  id: string;
  surveyId: string;
  surveyTitle: string;
  customerName: string;
  customerPhone: string;
  sentAt: string;
  status: 'Gönderildi' | 'Okundu' | 'Cevaplandı';
}

export default function AnketlerPage() {
  const [surveyList, setSurveyList] = useState<Survey[]>(surveys);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [sendLogs, setSendLogs] = useState<SendLog[]>([
    { id: 'log-1', surveyId: 'srvy-1', surveyTitle: 'Müşteri Memnuniyet Anketi', customerName: 'Ayşe Yılmaz', customerPhone: '+90 532 123 4567', sentAt: '2026-05-08 14:30', status: 'Cevaplandı' },
    { id: 'log-2', surveyId: 'srvy-1', surveyTitle: 'Müşteri Memnuniyet Anketi', customerName: 'Mehmet Demir', customerPhone: '+90 533 987 6543', sentAt: '2026-05-07 11:15', status: 'Okundu' },
    { id: 'log-3', surveyId: 'srvy-2', surveyTitle: 'Hizmet Değerlendirme', customerName: 'Elif Kaya', customerPhone: '+90 534 555 1122', sentAt: '2026-05-06 16:45', status: 'Gönderildi' },
  ]);
  const [activeTab, setActiveTab] = useState<'anketler' | 'gonderimler'>('anketler');

  // Send modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendingSurvey, setSendingSurvey] = useState<Survey | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formQuestions, setFormQuestions] = useState<SurveyQuestion[]>([]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const openModal = (survey?: Survey) => {
    if (survey) {
      setEditingSurvey(survey);
      setFormTitle(survey.title);
      setFormDesc(survey.description);
      setFormActive(survey.active);
      setFormQuestions([...survey.questions]);
    } else {
      setEditingSurvey(null);
      setFormTitle('');
      setFormDesc('');
      setFormActive(true);
      setFormQuestions([]);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingSurvey(null);
  };

  const openSendModal = (survey: Survey) => {
    if (!survey.active) {
      showToast('Pasif anketi gönderemezsiniz', 'error');
      return;
    }
    setSendingSurvey(survey);
    setCustomerSearch('');
    setSelectedCustomerIds([]);
    setSendModalOpen(true);
  };

  const closeSendModal = () => {
    setSendModalOpen(false);
    setSendingSurvey(null);
    setSelectedCustomerIds([]);
  };

  const toggleCustomer = (id: string) => {
    setSelectedCustomerIds(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const selectAllCustomers = () => {
    const allIds = filteredCustomers.map(c => c.id);
    const allSelected = allIds.every(id => selectedCustomerIds.includes(id));
    setSelectedCustomerIds(allSelected ? [] : allIds);
  };

  const handleSend = () => {
    if (!sendingSurvey || selectedCustomerIds.length === 0) {
      showToast('En az bir müşteri seçin', 'error');
      return;
    }

    const selectedCustomers = customers.filter(c => selectedCustomerIds.includes(c.id));

    // Her müşteri için wa.me linki aç (WhatsApp Web / mobil WhatsApp)
    selectedCustomers.forEach((customer, index) => {
      setTimeout(() => {
        const phone = customer.phone.replace(/[^0-9]/g, ''); // +, boşluk temizle
        const firstName = customer.fullName.split(' ')[0];
        const message = `Merhaba ${firstName}, Kugu Spa'dan yazıyoruz. "${sendingSurvey.title}" anketi için birkaç dakikanızı ayırabilir misiniz? Teşekkürler!`;
        const waLink = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        window.open(waLink, '_blank');
      }, index * 600);
    });

    // Gönderim geçmişine kaydet
    const newLogs: SendLog[] = selectedCustomers.map(c => ({
      id: `log-${Date.now()}-${c.id}`,
      surveyId: sendingSurvey.id,
      surveyTitle: sendingSurvey.title,
      customerName: c.fullName,
      customerPhone: c.phone,
      sentAt: new Date().toISOString().replace('T', ' ').slice(0, 16),
      status: 'Gönderildi',
    }));

    setSendLogs(prev => [...newLogs, ...prev]);
    showToast(`${selectedCustomers.length} müşteriye WhatsApp'tan anket gönderildi`);
    closeSendModal();
  };

  const handleSave = () => {
    if (!formTitle.trim()) {
      showToast('Anket başlığı zorunludur', 'error');
      return;
    }
    if (formQuestions.length === 0) {
      showToast('En az bir soru ekleyin', 'error');
      return;
    }
    if (formQuestions.some(q => !q.text.trim())) {
      showToast('Tüm soruların metni doldurulmalıdır', 'error');
      return;
    }

    const payload: Survey = {
      id: editingSurvey ? editingSurvey.id : `srvy-${Date.now()}`,
      title: formTitle.trim(),
      description: formDesc.trim(),
      active: formActive,
      createdAt: editingSurvey ? editingSurvey.createdAt : new Date().toISOString().split('T')[0],
      responseCount: editingSurvey ? editingSurvey.responseCount : 0,
      questions: formQuestions.map((q, idx) => ({
        ...q,
        id: q.id || `q-${Date.now()}-${idx}`,
      })),
    };

    if (editingSurvey) {
      setSurveyList(prev => prev.map(s => s.id === editingSurvey.id ? payload : s));
      showToast('Anket güncellendi');
    } else {
      setSurveyList(prev => [payload, ...prev]);
      showToast('Yeni anket oluşturuldu');
    }
    closeModal();
  };

  const handleDelete = (id: string) => {
    if (confirm('Bu anketi silmek istediğinize emin misiniz?')) {
      setSurveyList(prev => prev.filter(s => s.id !== id));
      showToast('Anket silindi');
    }
  };

  const toggleActive = (id: string) => {
    setSurveyList(prev => prev.map(s => s.id === id ? { ...s, active: !s.active } : s));
    const s = surveyList.find(x => x.id === id);
    showToast(s?.active ? 'Anket devre dışı bırakıldı' : 'Anket aktif edildi');
  };

  const addQuestion = (type: QuestionType) => {
    const newQ: SurveyQuestion = {
      id: `q-${Date.now()}-${formQuestions.length}`,
      text: '',
      type,
      required: true,
      options: type === 'multiple_choice' ? ['Seçenek 1'] : undefined,
    };
    setFormQuestions(prev => [...prev, newQ]);
  };

  const updateQuestion = (idx: number, updates: Partial<SurveyQuestion>) => {
    setFormQuestions(prev => prev.map((q, i) => i === idx ? { ...q, ...updates } : q));
  };

  const removeQuestion = (idx: number) => {
    setFormQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const addOption = (qIdx: number) => {
    setFormQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      return { ...q, options: [...(q.options || []), `Seçenek ${(q.options?.length || 0) + 1}`] };
    }));
  };

  const updateOption = (qIdx: number, optIdx: number, value: string) => {
    setFormQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...(q.options || [])];
      opts[optIdx] = value;
      return { ...q, options: opts };
    }));
  };

  const removeOption = (qIdx: number, optIdx: number) => {
    setFormQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      return { ...q, options: (q.options || []).filter((_, j) => j !== optIdx) };
    }));
  };

  const filteredSurveys = useMemo(() => {
    let data = [...surveyList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(s => s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q));
    }
    if (statusFilter === 'active') data = data.filter(s => s.active);
    if (statusFilter === 'inactive') data = data.filter(s => !s.active);
    return data;
  }, [surveyList, searchQuery, statusFilter]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers.filter(c => c.status === 'Aktif');
    const q = customerSearch.toLowerCase();
    return customers.filter(c => c.status === 'Aktif' && (c.fullName.toLowerCase().includes(q) || c.phone.includes(q)));
  }, [customerSearch]);

  const filteredLogs = useMemo(() => {
    let data = [...sendLogs];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(l => l.surveyTitle.toLowerCase().includes(q) || l.customerName.toLowerCase().includes(q));
    }
    return data;
  }, [sendLogs, searchQuery]);

  const stats = useMemo(() => ({
    total: surveyList.length,
    active: surveyList.filter(s => s.active).length,
    inactive: surveyList.filter(s => !s.active).length,
    totalResponses: surveyList.reduce((sum, s) => sum + s.responseCount, 0),
    totalSends: sendLogs.length,
  }), [surveyList, sendLogs]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all"
            style={{ background: t.type === 'success' ? '#10B981' : '#EF4444', color: '#fff' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} /></div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Anketler</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{activeTab === 'anketler' ? filteredSurveys.length : filteredLogs.length} kayıt</p>
        </div>
        {activeTab === 'anketler' && (
          <button onClick={() => openModal()} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
            Yeni Anket
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Toplam Anket', value: stats.total, icon: 'ri-survey-line', color: '#C9A962' },
          { label: 'Aktif', value: stats.active, icon: 'ri-check-line', color: '#10B981' },
          { label: 'Pasif', value: stats.inactive, icon: 'ri-close-line', color: '#EF4444' },
          { label: 'Toplam Cevap', value: stats.totalResponses, icon: 'ri-message-3-line', color: '#8B5CF6' },
          { label: 'Gönderim', value: stats.totalSends, icon: 'ri-send-plane-line', color: '#3B82F6' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${s.icon} text-lg`} style={{ color: s.color }} /></div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
        <button onClick={() => setActiveTab('anketler')} className="flex-1 text-sm py-2 rounded-full cursor-pointer transition-all"
          style={{ background: activeTab === 'anketler' ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))' : 'transparent', color: activeTab === 'anketler' ? '#fff' : 'var(--text-secondary)', fontWeight: activeTab === 'anketler' ? 600 : 400 }}>
          Anketler
        </button>
        <button onClick={() => setActiveTab('gonderimler')} className="flex-1 text-sm py-2 rounded-full cursor-pointer transition-all"
          style={{ background: activeTab === 'gonderimler' ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))' : 'transparent', color: activeTab === 'gonderimler' ? '#fff' : 'var(--text-secondary)', fontWeight: activeTab === 'gonderimler' ? 600 : 400 }}>
          Gönderim Geçmişi
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder={activeTab === 'anketler' ? 'Anket başlığı veya açıklama ara...' : 'Anket veya müşteri ara...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        {activeTab === 'anketler' && (
          <div className="flex gap-2 flex-wrap">
            <select className="spa-input text-sm min-w-[120px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}>
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        )}
      </div>

      {/* Anketler Tab */}
      {activeTab === 'anketler' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredSurveys.map(survey => (
            <div key={survey.id} className="spa-card p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{survey.title}</p>
                    <StatusBadge status={survey.active ? 'Aktif' : 'Pasif'}>{survey.active ? 'Aktif' : 'Pasif'}</StatusBadge>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{survey.description}</p>
                </div>
                <button onClick={() => toggleActive(survey.id)} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer ml-2 flex-shrink-0" style={{ background: 'var(--bg-tertiary)' }} title={survey.active ? 'Pasif yap' : 'Aktif yap'}>
                  <i className={survey.active ? 'ri-toggle-fill text-sm' : 'ri-toggle-line text-sm'} style={{ color: survey.active ? '#10B981' : '#9CA3AF' }} />
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center"><i className="ri-questionnaire-line text-xs" /></div>
                  {survey.questions.length} soru
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center"><i className="ri-message-3-line text-xs" /></div>
                  {survey.responseCount} cevap
                </span>
                <span className="ml-auto">{survey.createdAt}</span>
              </div>
              <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <button onClick={() => openModal(survey)} className="flex-1 spa-btn-secondary text-xs py-2">Düzenle</button>
                <button onClick={() => openSendModal(survey)} className="spa-btn-primary text-xs py-2 px-3 flex items-center gap-1 whitespace-nowrap" title="WhatsApp ile Gönder">
                  <div className="w-3 h-3 flex items-center justify-center"><i className="ri-send-plane-line" /></div>
                  Gönder
                </button>
                <button onClick={() => handleDelete(survey.id)} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }} title="Sil">
                  <i className="ri-delete-bin-line text-sm" style={{ color: '#EF4444' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'anketler' && filteredSurveys.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-survey-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Anket bulunamadı</p>
        </div>
      )}

      {/* Gönderim Geçmişi Tab */}
      {activeTab === 'gonderimler' && (
        <div className="spa-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Anket</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Müşteri</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Telefon</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Gönderim Tarihi</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={log.id} className="border-b hover:bg-black/5 transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>{log.surveyTitle}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{log.customerName}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{log.customerPhone}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{log.sentAt}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          background: log.status === 'Cevaplandı' ? 'rgba(16,185,129,0.12)' : log.status === 'Okundu' ? 'rgba(59,130,246,0.12)' : 'rgba(156,163,175,0.12)',
                          color: log.status === 'Cevaplandı' ? '#10B981' : log.status === 'Okundu' ? '#3B82F6' : '#6B7280',
                        }}>
                        <div className="w-2 h-2 rounded-full" style={{ background: log.status === 'Cevaplandı' ? '#10B981' : log.status === 'Okundu' ? '#3B82F6' : '#6B7280' }} />
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredLogs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                <i className="ri-send-plane-line text-xl" style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Henüz gönderim yapılmamış</p>
            </div>
          )}
        </div>
      )}

      {/* Anket Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeModal}>
          <div className="spa-card w-full max-w-2xl flex flex-col max-h-[90vh] my-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{editingSurvey ? 'Anket Düzenle' : 'Yeni Anket'}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              {/* Title / Desc */}
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Anket Başlığı <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input w-full text-sm" value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Örn. Müşteri Memnuniyet Anketi" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Açıklama</label>
                <input className="spa-input w-full text-sm" value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Anketin kısa açıklaması..." />
              </div>
              <div className="flex items-center gap-2">
                <input id="surveyActive" type="checkbox" className="w-4 h-4" style={{ accentColor: '#C9A962' }} checked={formActive} onChange={e => setFormActive(e.target.checked)} />
                <label htmlFor="surveyActive" className="text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>Anket şu anda aktif</label>
              </div>

              {/* Question Builder */}
              <div className="pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Sorular ({formQuestions.length})</h3>
                  <div className="flex gap-1 flex-wrap">
                    {([
                      { type: 'rating' as QuestionType, icon: 'ri-star-line', label: 'Puanlama' },
                      { type: 'multiple_choice' as QuestionType, icon: 'ri-checkbox-multiple-line', label: 'Çoktan Seçmeli' },
                      { type: 'text' as QuestionType, icon: 'ri-text', label: 'Yazı' },
                      { type: 'yes_no' as QuestionType, icon: 'ri-toggle-line', label: 'Evet/Hayır' },
                    ]).map(btn => (
                      <button key={btn.type} onClick={() => addQuestion(btn.type)} className="flex items-center gap-1 px-2 py-1 rounded-spa-sm text-xs cursor-pointer transition-colors" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                        <div className="w-3 h-3 flex items-center justify-center"><i className={btn.icon} /></div>
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  {formQuestions.map((q, idx) => (
                    <div key={q.id} className="p-3 rounded-spa-sm border space-y-2" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold text-white flex-shrink-0" style={{ background: 'var(--gold)' }}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <input className="spa-input w-full text-sm" value={q.text} onChange={e => updateQuestion(idx, { text: e.target.value })} placeholder="Soru metni..." />
                        </div>
                        <button onClick={() => updateQuestion(idx, { required: !q.required })} className="text-xs px-2 py-1 rounded-spa-sm cursor-pointer" style={{ background: q.required ? 'rgba(201,169,98,0.15)' : 'var(--bg-tertiary)', color: q.required ? 'var(--gold)' : 'var(--text-muted)' }}>
                          {q.required ? 'Zorunlu' : 'Opsiyonel'}
                        </button>
                        <button onClick={() => removeQuestion(idx)} className="w-6 h-6 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }}>
                          <i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 ml-8">
                        <div className="w-4 h-4 flex items-center justify-center"><i className={`${questionTypeIcons[q.type]} text-xs`} style={{ color: 'var(--text-muted)' }} /></div>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{questionTypeLabels[q.type]}</span>
                      </div>
                      {/* Multiple choice options */}
                      {q.type === 'multiple_choice' && (
                        <div className="ml-8 space-y-1">
                          {q.options?.map((opt, optIdx) => (
                            <div key={optIdx} className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full border" style={{ borderColor: 'var(--border-color)' }} />
                              <input className="spa-input text-xs flex-1 py-1" value={opt} onChange={e => updateOption(idx, optIdx, e.target.value)} />
                              {q.options!.length > 1 && (
                                <button onClick={() => removeOption(idx, optIdx)} className="w-5 h-5 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'rgba(239,68,68,0.08)' }}>
                                  <i className="ri-close-line text-xs" style={{ color: '#EF4444' }} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button onClick={() => addOption(idx)} className="text-xs flex items-center gap-1 cursor-pointer mt-1" style={{ color: 'var(--gold)' }}>
                            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line" /></div>
                            Seçenek ekle
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {formQuestions.length === 0 && (
                    <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>Henüz soru eklenmemiş. Yukarıdaki butonlardan soru tipi seçin.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={closeModal} className="spa-btn-outline text-sm">İptal</button>
              <button onClick={handleSave} className="spa-btn-primary text-sm">{editingSurvey ? 'Güncelle' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Send WhatsApp Modal */}
      {sendModalOpen && sendingSurvey && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeSendModal}>
          <div className="spa-card w-full max-w-lg flex flex-col max-h-[90vh] my-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <div>
                <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>WhatsApp ile Gönder</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sendingSurvey.title} — {sendingSurvey.questions.length} soru</p>
              </div>
              <button onClick={closeSendModal} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              {/* Customer search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
                <input className="spa-input pl-10 w-full text-sm" placeholder="Müşteri adı veya telefon ara..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </div>

              {/* Select all */}
              <div className="flex items-center gap-2 px-1">
                <input id="selectAll" type="checkbox" className="w-4 h-4" style={{ accentColor: '#C9A962' }}
                  checked={filteredCustomers.length > 0 && filteredCustomers.every(c => selectedCustomerIds.includes(c.id))}
                  onChange={selectAllCustomers} />
                <label htmlFor="selectAll" className="text-xs font-medium cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                  Tümünü Seç ({filteredCustomers.length} müşteri)
                </label>
                {selectedCustomerIds.length > 0 && (
                  <span className="text-xs ml-auto" style={{ color: 'var(--gold)' }}>{selectedCustomerIds.length} seçili</span>
                )}
              </div>

              {/* Customer list */}
              <div className="space-y-1 max-h-[320px] overflow-y-auto">
                {filteredCustomers.map(customer => (
                  <div key={customer.id} onClick={() => toggleCustomer(customer.id)}
                    className="flex items-center gap-3 p-2.5 rounded-spa-sm cursor-pointer hover:bg-black/5 transition-colors"
                    style={{ background: selectedCustomerIds.includes(customer.id) ? 'rgba(201,169,98,0.08)' : 'var(--bg-tertiary)' }}>
                    <input type="checkbox" className="w-4 h-4 flex-shrink-0" style={{ accentColor: '#C9A962' }}
                      checked={selectedCustomerIds.includes(customer.id)} onChange={() => toggleCustomer(customer.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{customer.fullName}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{customer.phone}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>
                      {customer.totalVisits} ziyaret
                    </span>
                  </div>
                ))}
                {filteredCustomers.length === 0 && (
                  <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>Müşteri bulunamadı</p>
                )}
              </div>

              {/* Preview */}
              {selectedCustomerIds.length > 0 && sendingSurvey && (
                <div className="p-3 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'rgba(16,185,129,0.04)' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>WhatsApp Mesaj Önizleme:</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Merhaba, Kugu Spa'dan yazıyoruz. "{sendingSurvey.title}" anketi için birkaç dakikanızı ayırabilir misiniz? Teşekkürler!
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--gold)' }}>
                    <i className="ri-whatsapp-line mr-1" /> Her müşteri için ayrı WhatsApp sekmesi açılacak
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={closeSendModal} className="spa-btn-outline text-sm">İptal</button>
              <button onClick={handleSend} className="spa-btn-primary text-sm flex items-center gap-2" disabled={selectedCustomerIds.length === 0}>
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-whatsapp-line" /></div>
                {selectedCustomerIds.length > 0 ? `${selectedCustomerIds.length} Kişiye Gönder` : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}